import {
  Briefcase,
  Building2,
  GraduationCap,
  HeartPulse,
  MessageSquare,
  Shield,
  Users,
  UserRound,
  Vote,
} from 'lucide-react'
import type { AkimSectorDefinition, AkimSectorKey } from '@/types/akimSector'

/** Единый набор вкладок: сводка (метрики + графики) и районы. */
const AKIM_TABS = [
  { id: 'overview' as const, label: 'Сводка' },
  { id: 'districts' as const, label: 'Районы' },
]

/**
 * Каталог `/modules` — 9 блоков панели акима области.
 * Второй уровень (сегменты) — фильтр сверху внутри модуля, не отдельный каталог.
 */
export const akimSectorDefinitions: AkimSectorDefinition[] = [
  {
    key: 'education',
    title: 'Образование',
    subtitle: 'НОБД · сеть и контингент',
    desc: 'Обучающиеся, организации НОБД, зачисления и сравнение районов области.',
    // Цвета разделов — цвета направлений веба: бирюза — социальная сфера,
    // янтарь — экономика, красный — безопасность, фиолетовый — госуправление.
    backgroundColor: '#0e9f90',
    icon: GraduationCap,
    tabs: AKIM_TABS,
    dataMode: 'live',
  },
  {
    key: 'labor',
    title: 'Труд и занятость',
    subtitle: 'ВЛИ · работодатели',
    desc: 'Работодатели, задолженность ЗП, ЭТД и ОСНС по области; районы — оценка доли.',
    backgroundColor: '#c47c10',
    icon: Briefcase,
    tabs: AKIM_TABS,
    segments: [
      { value: 'all', label: 'ЮЛ + ИП' },
      { value: 'ul', label: 'ЮЛ' },
      { value: 'ip', label: 'ИП' },
    ],
    dataMode: 'live',
  },
  {
    key: 'healthcare',
    title: 'Здравоохранение',
    subtitle: 'Реестр МЗ · нагрузка',
    desc: 'Реестр пациентов МЗ, диагнозы, медорганизации и сравнение районов области.',
    backgroundColor: '#0e9f90',
    icon: HeartPulse,
    tabs: AKIM_TABS,
    dataMode: 'live',
  },
  {
    key: 'population',
    title: 'Население и демография',
    subtitle: 'ЗАГС · ГБД ФЛ',
    desc: 'Численность, рождения и браки, возрастной профиль и районы области.',
    backgroundColor: '#0e9f90',
    icon: UserRound,
    tabs: AKIM_TABS,
    dataMode: 'mixed',
  },
  {
    key: 'appeals',
    title: 'Обращения',
    subtitle: 'е-Отиныш',
    desc: 'Поток обращений, темы, характер документов и разрез по районам (КАТО).',
    backgroundColor: '#6b5bd6',
    icon: MessageSquare,
    tabs: AKIM_TABS,
    dataMode: 'mixed',
  },
  {
    key: 'public-safety',
    title: 'Общественная безопасность',
    subtitle: 'КУИ · адм. нарушения',
    desc: 'Заявления КУИ по типам, протоколы ЕРАП и сравнение районов области.',
    backgroundColor: '#d63a44',
    icon: Shield,
    tabs: AKIM_TABS,
    dataMode: 'live',
  },
  {
    key: 'social-protection',
    title: 'Социальная защита',
    subtitle: 'ГБД ФЛ · уязвимые группы',
    desc: 'ГБД ФЛ: пожилые, дети в трудной ситуации, ЦБДИ; АСП — витрина. Разрез по районам области.',
    backgroundColor: '#0e9f90',
    icon: Users,
    tabs: AKIM_TABS,
    dataMode: 'live',
  },
  {
    key: 'business-investors',
    title: 'Бизнес и инвесторы',
    subtitle: 'Проекты и предприниматели',
    desc: 'Инвестиционные проекты, поддержка бизнеса и ход реализации по области.',
    backgroundColor: '#c47c10',
    icon: Building2,
    tabs: AKIM_TABS,
    segments: [
      { value: 'all', label: 'Все направления' },
      { value: 'invest', label: 'Инвестиции' },
      { value: 'business', label: 'Бизнес' },
    ],
    dataMode: 'mixed',
  },
  {
    key: 'elections',
    title: 'Выборы',
    subtitle: 'Сверка списков избирателей',
    desc: 'Избиратели по районам, возраст и национальности, участки и поиск участка по адресу.',
    backgroundColor: '#0e9f90',
    icon: Vote,
    tabs: AKIM_TABS,
    dataMode: 'live',
  },
]

export function getAkimSector(key: string): AkimSectorDefinition | undefined {
  return akimSectorDefinitions.find((s) => s.key === key)
}

export function isAkimSectorKey(key: string): key is AkimSectorKey {
  return akimSectorDefinitions.some((s) => s.key === key)
}
